import React, { useState } from 'react';
import { BulkGenerateFromPayroll } from '@/api/Payroll/userPayslip';
import { BulkGeneratePayslipsInput } from '@/models/payroll';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/Card';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import AppIcon from '@/components/AppIcon';

interface GenerationOptions {
  includeVariablePay: boolean;
  includeAdjustmentAdditions: boolean;
  includeAdjustmentDeductions: boolean;
  includeTaxCalculations: boolean;
  sendEmailNotification: boolean;
  generatePdfs: boolean;
}

interface DepartmentOption {
  id: number;
  name: string;
}

interface BulkGenerationPanelProps {
  onBulkGenerate?: (input: any) => Promise<void>;
  isLoading?: boolean;
  departments?: string[];
  departmentOptions?: DepartmentOption[];
  loadingDepartments?: boolean;
}

const BulkGenerationPanel: React.FC<BulkGenerationPanelProps> = ({
  onBulkGenerate,
  isLoading: externalLoading,
  departments = [],
  departmentOptions: deptOptions = [],
  loadingDepartments = false
}) => {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    text: string;
  } | null>(null);
  const [generationOptions, setGenerationOptions] = useState<GenerationOptions>({
    includeVariablePay: true,
    includeAdjustmentAdditions: true,
    includeAdjustmentDeductions: true,
    includeTaxCalculations: true,
    sendEmailNotification: true,
    generatePdfs: true
  });

  const loading = externalLoading || isGenerating;

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const months = [];
    const currentDate = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      const value = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;

      months.push({
        value,
        label: `${month} ${year}`
      });
    }

    return months;
  };

  // Department options - use ID-based values when available
  const departmentOptions = [
    { value: 'all', label: t('All Departments') },
    ...(deptOptions.length > 0
      ? deptOptions.map(dept => ({ value: dept.id.toString(), label: dept.name }))
      : departments.map(dept => ({ value: dept, label: dept })))
  ];

  const handleBulkGenerate = async () => {
    // Clear any existing messages
    setMessage(null);

    // Enhanced validation
    if (!selectedMonth) {
      setMessage({ type: 'error', text: t('Please select a month and year for payroll generation') });
      return;
    }

    // Validate department selection
    const validDeptValues = deptOptions.length > 0
      ? deptOptions.map(d => d.id.toString())
      : departments;
    if (selectedDepartment && selectedDepartment !== 'all' && !validDeptValues.includes(selectedDepartment)) {
      setMessage({ type: 'error', text: t('Selected department is not valid. Please choose a valid department.') });
      return;
    }

    // Check if departments are loaded
    if (!loadingDepartments && departments.length === 0 && selectedDepartment !== 'all') {
      setMessage({ type: 'warning', text: t('No departments available. You can only generate for all departments.') });
      return;
    }

    setIsGenerating(true);

    try {
      const [year, month] = selectedMonth.split('-');
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                         'July', 'August', 'September', 'October', 'November', 'December'];
      const monthName = t(monthNames[parseInt(month) - 1]);

      // Show initial processing message
      const deptLabel = selectedDepartment === 'all'
        ? t('all departments')
        : departmentOptions.find(d => d.value === selectedDepartment)?.label || selectedDepartment;
      setMessage({
        type: 'info',
        text: t('Starting payslip generation for {{target}} - {{month}} {{year}}...', { target: deptLabel, month: monthName, year })
      });

      // Format the input to match what the server expects - including generation options
      const input: any = {
        month: month,
        year: year,
        generatePdf: generationOptions.generatePdfs,
        sendEmail: generationOptions.sendEmailNotification,
        // Include generation options
        includeVariablePay: generationOptions.includeVariablePay,
        includeAdjustmentAdditions: generationOptions.includeAdjustmentAdditions,
        includeAdjustmentDeductions: generationOptions.includeAdjustmentDeductions,
        includeDeductions: true,
        includeTaxCalculations: generationOptions.includeTaxCalculations
      };


      // Add department if not "all" — ensure numeric ID
      if (selectedDepartment && selectedDepartment !== 'all') {
        const deptId = parseInt(selectedDepartment, 10);
        input.department = isNaN(deptId) ? selectedDepartment : deptId;
      }

      // Use prop function if provided, otherwise use default API call
      if (onBulkGenerate) {
        await onBulkGenerate(input);
        // Clear the processing message since parent will handle success/error
        setMessage(null);
      } else {
        const apiInput: BulkGeneratePayslipsInput = {
          month: parseInt(month),
          year: parseInt(year),
          department: selectedDepartment !== 'all' ? parseInt(selectedDepartment) || undefined : undefined,
          includeVariablePay: generationOptions.includeVariablePay,
          includeAdjustmentAdditions: generationOptions.includeAdjustmentAdditions,
          includeAdjustmentDeductions: generationOptions.includeAdjustmentDeductions,
          includeDeductions: true,
          includeTaxCalculations: generationOptions.includeTaxCalculations,
          generatePdf: generationOptions.generatePdfs,
          sendEmailNotification: generationOptions.sendEmailNotification
        };
        const result = await BulkGenerateFromPayroll(apiInput);
        if (result.status === 200) {
          setMessage({
            type: 'success',
            text: t('Successfully initiated payslip generation for {{target}} - {{month}} {{year}}', { target: deptLabel, month: monthName, year })
          });
        } else {
          const errorDetail = result.errors?.[0]?.description || t('Unknown error occurred');
          setMessage({
            type: 'error',
            text: t('Failed to generate payslips: {{error}}', { error: errorDetail })
          });
        }
      }

      // Reset form after successful generation (only if using local API call)
      if (!onBulkGenerate) {
        setTimeout(() => {
          setSelectedMonth('');
          setSelectedDepartment('all');
          setMessage(null);
        }, 3000);
      }
    } catch (error: any) {
      console.error('Bulk generation failed:', error);

      let errorText = t('Failed to generate payslips');

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 404) {
          errorText = t('Payslip generation service is not available');
        } else if (status === 403) {
          errorText = t("You don't have permission to generate payslips");
        } else if (status === 400) {
          errorText = data?.errors?.[0]?.description || data?.message || t('Invalid request parameters');
        } else if (status >= 500) {
          errorText = t('Server error occurred. Please try again later');
        } else {
          errorText = data?.message || t('Request failed');
        }
      } else if (error.request) {
        errorText = t('Network error. Please check your connection');
      }

      setMessage({
        type: 'error',
        text: errorText
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOptionChange = (option: keyof GenerationOptions) => {
    setGenerationOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const monthOptions = getMonthOptions();

  return (
    <Card variant="elevated" className="mb-6" data-testid="bulk-generation-panel">
      <CardContent>
        <div className="flex items-center mb-6">
          <AppIcon name="FileText" size={24} className="me-2 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            {t('Bulk Payslip Generation')}
          </h3>
        </div>

        {message && (
          <div className="mb-4">
            <Alert variant={message.type} onClose={() => setMessage(null)}>
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Selection Options */}
          <div className="space-y-4">
            <Select
              label={t('Payroll Month')}
              options={monthOptions}
              value={selectedMonth}
              onChange={(val: string) => setSelectedMonth(val)}
              placeholder={t('Select month')}
              disabled={isGenerating}
            />

            <Select
              label={t('Department')}
              options={departmentOptions}
              value={selectedDepartment}
              onChange={(val: string) => setSelectedDepartment(val)}
              disabled={loadingDepartments || loading}
              loading={loadingDepartments}
            />

            <Button
              variant="default"
              onClick={handleBulkGenerate}
              disabled={isGenerating || !selectedMonth}
              fullWidth
              loading={isGenerating}
              iconName="Play"
              iconPosition="left"
              data-testid="generate-btn"
            >
              {isGenerating ? t('Generating...') : t('Generate Payslips')}
            </Button>
          </div>

          {/* Generation Options */}
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">
              {t('Generation Options')}
            </p>

            <div className="flex flex-col gap-2">
              {[
                { key: 'includeVariablePay' as const, label: 'Include Variable Pay', icon: 'PlusCircle' },
                { key: 'includeAdjustmentAdditions' as const, label: 'Include Adjustment Additions', icon: 'Plus' },
                { key: 'includeAdjustmentDeductions' as const, label: 'Include Adjustment Deductions', icon: 'Minus' },
                //{ key: 'includeDeductions' as const, label: 'Include Deductions', icon: 'MinusCircle' },
                //{ key: 'includeTaxCalculations' as const, label: 'Include Tax Calculations', icon: 'Calculator' },
                { key: 'generatePdfs' as const, label: 'Generate PDF Files', icon: 'File' },
                { key: 'sendEmailNotification' as const, label: 'Send Email Notifications', icon: 'Mail' }
              ].map(option => (
                <div
                  key={option.key}
                  className="flex items-center gap-2 py-1"
                >
                  <Checkbox
                    label={t(option.label)}
                    checked={generationOptions[option.key]}
                    onChange={() => handleOptionChange(option.key)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-border my-4" />

        {/* Stats / Info */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground flex items-center">
            <AppIcon name="Info" size={14} className="me-1.5" />
            {t('Generates payslips for all active employees in the selected period')}
          </p>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMonth('');
              setSelectedDepartment('all');
              setGenerationOptions({
                includeVariablePay: true,
                includeAdjustmentAdditions: true,
                includeAdjustmentDeductions: true,
                includeDeductions: true,
                includeTaxCalculations: true,
                sendEmailNotification: true,
                generatePdfs: true
              });
            }}
            disabled={isGenerating}
          >
            {t('Clear All')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkGenerationPanel;
